import React, { useMemo } from 'react';
import type { IconButtonProps } from '@mui/material';
import { IconButton, Tooltip } from '@mui/material';
import { NotificationImportant, Troubleshoot } from '@mui/icons-material';
import { useBoolean } from 'react-hanger';
import type { LineItemUnderInvestigation, TalonOrderlineError } from 'c-sdk';
import { DialogV2, Table, TableBody, TableCell, TableHead, TableRow } from 'c-components';
import { useCommonTranslation } from 'c-translation';
import { useLanguageSwitcher } from 'c-hooks';
import { LANG_en_gb } from 'c-lib';

/**
 * The icons a Talon campaign's reporting page shows beside its filters, each opening a dialog
 * listing the issue. Shared with the live dashboard, which gets the same rows from `reporting/live`.
 */

type ButtonProps = {
    size?: IconButtonProps['size'];
};

const useLocaleLang = () => {
    const { lang } = useLanguageSwitcher();
    return useMemo(() => (lang === LANG_en_gb ? 'en-GB' : 'en-US'), [lang]);
};

const formatDate = (value: any): string => {
    if (!value) return '';
    const dateString = typeof value === 'string' ? value : value.date ?? '';
    if (!dateString) return '';
    return new Date(dateString).toLocaleString();
};

export const LineItemsUnderInvestigationButton: React.FC<
    ButtonProps & { rows: LineItemUnderInvestigation[] }
> = ({ rows, size = 'large' }) => {
    const t = useCommonTranslation();
    const localeLang = useLocaleLang();
    const dialogState = useBoolean(false);

    const formatCount = (value: number) =>
        (value ?? 0).toLocaleString(localeLang, { maximumFractionDigits: 0 });

    return (
        <>
            <Tooltip
                title={t('Modules.Main.Campaigns.Overview.Reports.lineItemsUnderInvestigation')}
            >
                <IconButton onClick={dialogState.setTrue} color="primary" size={size}>
                    <Troubleshoot fontSize={size === 'small' ? 'small' : undefined} />
                </IconButton>
            </Tooltip>
            <DialogV2
                onClose={dialogState.setFalse}
                open={dialogState.value}
                maxWidth="xl"
                title="Modules.Main.Campaigns.Overview.Reports.lineItemsUnderInvestigation"
            >
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>
                                {t('Modules.Main.Campaigns.DataStatusLineItems.mediaOwner')}
                            </TableCell>
                            <TableCell>
                                {t('Modules.Main.Campaigns.DataStatusLineItems.lineItemName')}
                            </TableCell>
                            <TableCell align="right">
                                {t('Modules.Main.Campaigns.DataStatusLineItems.totalPlays')}
                            </TableCell>
                            <TableCell align="right">
                                {t('Modules.Main.Campaigns.DataStatusLineItems.expectedPlays')}
                            </TableCell>
                            <TableCell align="right">
                                {t('Modules.Main.Campaigns.DataStatusLineItems.totalImpacts')}
                            </TableCell>
                            <TableCell align="right">
                                {t('Modules.Main.Campaigns.DataStatusLineItems.expectedImpacts')}
                            </TableCell>
                            <TableCell align="right">
                                {t('Modules.Main.Campaigns.DataStatusLineItems.impacts')}
                            </TableCell>
                            <TableCell align="right">
                                {t('Modules.Main.Campaigns.DataStatusLineItems.plays')}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map(row => (
                            <TableRow key={row?.id}>
                                <TableCell>{row?.display_media_owner_name}</TableCell>
                                <TableCell>{row?.display_line_item_name}</TableCell>
                                <TableCell align="right">
                                    {formatCount(row?.plays_received)}
                                </TableCell>
                                <TableCell align="right">
                                    {formatCount(row?.expected_plays)}
                                </TableCell>
                                <TableCell align="right">
                                    {formatCount(row?.impacts_received)}
                                </TableCell>
                                <TableCell align="right">
                                    {formatCount(row?.expected_impacts)}
                                </TableCell>
                                <TableCell align="right">
                                    {Math.round((row.impacts_percentage ?? 0) * 100)}%
                                </TableCell>
                                <TableCell align="right">
                                    {Math.round((row.plays_percentage ?? 0) * 100)}%
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </DialogV2>
        </>
    );
};

export const OrderlineErrorsButton: React.FC<ButtonProps & { errors: TalonOrderlineError[] }> = ({
    errors,
    size = 'large',
}) => {
    const t = useCommonTranslation();
    const dialogState = useBoolean(false);

    return (
        <>
            <Tooltip title={t('Modules.Main.Campaigns.Overview.Reports.orderLineErrors')}>
                <IconButton onClick={dialogState.setTrue} color="primary" size={size}>
                    <NotificationImportant fontSize={size === 'small' ? 'small' : undefined} />
                </IconButton>
            </Tooltip>
            <DialogV2
                onClose={dialogState.setFalse}
                open={dialogState.value}
                maxWidth="xl"
                title="Modules.Main.Campaigns.Overview.Reports.orderLineErrors"
            >
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            {[
                                'orderlineId',
                                'message',
                                'mediaOwner',
                                'environment',
                                'format',
                                'startDate',
                                'endDate',
                                'quantity',
                                'updatedAt',
                                'tradingType',
                            ].map(column => (
                                <TableCell key={column}>
                                    {t(
                                        `Modules.Main.Campaigns.Overview.Reports.orderLineErrorsTable.${column}`,
                                    )}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {errors.map(error => (
                            <TableRow key={error.id ?? error.orderline_id}>
                                <TableCell>{error.orderline_id}</TableCell>
                                <TableCell>{error.message}</TableCell>
                                <TableCell>{error.media_owner}</TableCell>
                                <TableCell>{error.environment}</TableCell>
                                <TableCell>{error.format_name}</TableCell>
                                <TableCell>{formatDate(error.line_start_date)}</TableCell>
                                <TableCell>{formatDate(error.line_end_date)}</TableCell>
                                <TableCell>{error.quantity}</TableCell>
                                <TableCell>
                                    {formatDate(
                                        error.row?.Booking_Amended_On ?? error.row?.Created_Date,
                                    )}
                                </TableCell>
                                <TableCell>{error.trading_type}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </DialogV2>
        </>
    );
};
